import {
  extend,
  filter,
  findLast,
  includes,
  isArrayLike,
  isObjectLike,
  map,
  orderBy,
  pull,
} from 'lodash'
import moment from 'moment-timezone'

import { Breadcrumb } from '../../data'
import { AppointmentEstimateSchema } from '../../schema'
import { getFacilityAndLocalTime } from '../../utils'
import { APPOINTMENT_REQUEST_STATUS } from '../appointment-request/'
import { AUTHORIZATION_STATUS } from '../auth-status'
import { AuthorizationRule } from '../authorization'
import { BaseModel } from '../base-model'
import { EpisodeModel } from '../episode'
import { EstimateBalanceWorkflowStatus } from '../estimate-balance'
import { Facility } from '../facility'
import { Invoice, InvoiceModel } from '../invoice'
import { Patient } from '../patient'
import { PatientAccountModel } from '../patient-account'
import { PatientInsurance } from '../patient-insurance'
import { PortalModel } from '../portal'
import { ProductData, ProductModel } from '../product'
import { REFERRAL_STATUS } from '../referral-status'
import { ReferrerModel } from '../referrer'
import { Resource, ResourceData } from '../resource'
import { StudyData } from '../study'
import { orderProductsByStudyStart } from './order-products-by-study'
import { TransactionData, TransactionModel } from '../transaction'
import { WorkItemData } from '../worklist'
import { AppointmentData } from './appointment-data'
import { type APPOINTMENT_WAIT_LIST_STATUS } from './appointment-waitlist-status'

const dayFormat = 'L' // 'MM//DD/YYYY'
const timeFormat = 'LT' // 'h:mm A'
export class AppointmentModel extends BaseModel implements Breadcrumb {
  diagnosisCodes?: string[]
  eid?: string
  ekey?: string
  pan?: string
  cancellationReason?: string
  authorizations?: any[]
  authorizationStatuses?: AUTHORIZATION_STATUS[]
  referrals?: any[]
  referralStatuses?: REFERRAL_STATUS[]
  /** @deprecated, use eligibilityDetails */
  eligibilityStatus?: Appointment.ELIGIBILITY_STATUS
  /** @deprecated, use eligibilityDetails */
  eligibilityId?: string
  eligibilityDetails?: Appointment.EligibilityDetails
  type: Appointment.TYPE
  startAt?: moment.Moment
  endAt?: moment.Moment
  duration?: number
  scheduledAt?: moment.Moment
  estimateId?: string
  estimate?: AppointmentEstimateSchema
  intakeId?: string
  intakeStatus?: Appointment.INTAKE_STATUS
  surveyStatus?: Appointment.SURVEY_STATUS
  intakeProgress?: number
  locationId?: string
  location?: Facility
  referrerId?: string
  referringPhysicianId?: string
  orderingPhysicianId?: string
  attendingPhysicianId?: string
  referrer?: ReferrerModel
  notes?: string
  request?: { id: string, status: APPOINTMENT_REQUEST_STATUS }
  waitList?: Appointment.WaitList
  orderingPhysician?: string
  patientId: string
  patient?: Patient
  paymentStatus?: Appointment.PAYMENT_STATUS
  estimationStatus?: Appointment.ESTIMATION_STATUS
  /** Server-owned snapshot kept by the Estimate and EstimateBalance streams (PIA-3105). */
  unifiedEstimatorWorkflowStatus?: EstimateBalanceWorkflowStatus | null
  priority: number
  productIds: string[] = []
  products: ProductModel[] = []
  productCategories: string[] = []
  invoices?: InvoiceModel[]
  status: Appointment.STATUS = Appointment.STATUS.OPEN
  statusDescription?: string
  transactions: TransactionModel[] = []
  transactionIds: string[] = []
  insurances: PatientInsurance[]
  policies?: PatientInsurance[]
  workItems: WorkItemData[] = []
  /** @deprecated, use eligibilityDetails */
  eligibilities: { [key: string]: string }
  resourceIds?: string[]
  resources: Resource[] = []
  customFields?: Record<string, any>
  metadata?: Record<string, any>
  patientConfirmedAt?: moment.Moment
  patientConfirmedBy?: string | 'sms' | 'link'
  patientConfirmed?: boolean
  lastConfirmMsgSentAt?: moment.Moment
  timezone?: string
  orderedBy?: string
  scheduledBy?: string
  studies?: StudyData[]
  portals?: PortalModel[]
  authRules?: AuthorizationRule[]
  accountId?: string
  account?: PatientAccountModel
  /**
   * If accountId is not set, you can create a new account along with the appointment by providing this field
   */
  newAccount?: Partial<{ eid: string, insurances: string[] }>
  episodeId?: string
  episode?: EpisodeModel

  constructor(data: AppointmentData) {
    super(data)
    this.type = Appointment.TYPE.APPOINTMENT
    this.type = data.type ?? Appointment.TYPE.APPOINTMENT
    this.eid = data.eid
    this.ekey = data.ekey
    this.pan = data.pan
    this.patientId = data.patientId
    this.locationId = data.locationId
    this.referrerId = data.referrerId
    this.referringPhysicianId = data.referringPhysicianId
    this.attendingPhysicianId = data.attendingPhysicianId
    this.endAt = data.endAt ? this.transformDate(data.endAt) : this.startAt
    this.duration = data.duration
    this.status = data.status as Appointment.STATUS
    this.statusDescription = data.statusDescription
    this.priority = data.priority || 0
    this.notes = data.notes
    this.request = data.request
    this.waitList = data.waitList
    this.authorizations = data.authorizations || []
    this.authorizationStatuses = data.authorizationStatuses || [AUTHORIZATION_STATUS.NONE]
    this.referrals = data.referrals || []
    this.referralStatuses = data.referralStatuses || [REFERRAL_STATUS.NONE]
    this.eligibilityStatus = data.eligibilityStatus as Appointment.ELIGIBILITY_STATUS
    this.eligibilityId = data.eligibilityId
    this.eligibilityDetails = data.eligibilityDetails || {}
    this.intakeStatus = data.intakeStatus as Appointment.INTAKE_STATUS
    this.intakeProgress = data.intakeProgress
    this.intakeId = data.intakeId
    this.estimateId = data.estimateId
    this.estimate = data.estimate
    this.paymentStatus = data.paymentStatus as Appointment.PAYMENT_STATUS
    this.estimationStatus = data.estimationStatus as Appointment.ESTIMATION_STATUS
    this.unifiedEstimatorWorkflowStatus = data.unifiedEstimatorWorkflowStatus ?? null
    this.cancellationReason = data.cancellationReason
    this.orderingPhysician = data.orderingPhysician
    this.orderingPhysicianId = data.orderingPhysicianId
    this.productCategories = data.productCategories || []
    this.insurances = orderBy((data.insurances || []).map((insurance) => new PatientInsurance(insurance)), i => i.typeIndex)
    if (data.policies) {
      this.policies = orderBy(data.policies.map((p) => new PatientInsurance(p)), i => i.typeIndex)
    }
    this.eligibilities = data.eligibilities || {}
    this.patientConfirmedBy = data.patientConfirmedBy
    this.customFields = data.customFields
    this.metadata = data.metadata
    this.timezone = data.timezone
    this.orderedBy = data.orderedBy
    this.scheduledBy = data.scheduledBy
    this.surveyStatus = data.surveyStatus
    this.workItems = data.workItems || []
    this.diagnosisCodes = data.diagnosisCodes
    this.studies = data.studies || []

    if (data.startAt && this.type !== Appointment.TYPE.ORDER) {
      this.startAt = this.transformDate(data.startAt)
    }

    if (data.patient) {
      this.patient = new Patient(data.patient)
    }

    if (data.location) {
      this.location = new Facility(data.location)

      if (!this.timezone && this.location.timezone) {
        this.timezone = this.location.timezone
      }
    }

    if (data.referrer) {
      this.referrer = new ReferrerModel(data.referrer)
    }

    if (data.scheduledAt) {
      this.scheduledAt = this.transformDate(data.scheduledAt)
    }

    if (data.patientConfirmedAt) {
      this.patientConfirmedAt = this.transformDate(data.patientConfirmedAt)
    }

    if (data.lastConfirmMsgSentAt) {
      this.lastConfirmMsgSentAt = this.transformDate(
        data.lastConfirmMsgSentAt,
      )
    }

    if (typeof data.patientConfirmed === 'boolean') {
      this.patientConfirmed = data.patientConfirmed
    }

    if (isArrayLike(data.products) && data.products.length > 0) {
      if (isObjectLike(data.products[0])) {
        this.products = map(
          data.products,
          (productData) => new ProductModel(productData as ProductData),
        )
        this.productIds = map(this.products, (product) => product.id)
      } else {
        this.productIds = data.products as string[]
      }
    }

    // Order procedures to match the studies (sorted by startAt), since products is an unordered Set (PIA-2633)
    if (this.products.length > 1) {
      this.products = orderProductsByStudyStart(this.products, this.studies)
      this.productIds = map(this.products, (product) => product.id)
    }

    if (isArrayLike(data.transactions) && data.transactions.length > 0) {
      if (isObjectLike(data.transactions[0])) {
        this.transactions = map(
          data.transactions,
          (transactionData) =>
            new TransactionModel(transactionData as TransactionData),
        )
        this.transactionIds = map(
          this.transactions,
          (transaction) => transaction.id,
        )
      } else {
        this.transactionIds = data.transactions as string[]
      }
    }

    if (isArrayLike(data.resources) && data.resources.length > 0) {
      if (isObjectLike(data.resources[0])) {
        this.resources = map(
          data.resources,
          (resourceData) => new Resource(resourceData as ResourceData),
        )
        this.resourceIds = map(this.resources, (resource) => resource.id)
      } else {
        this.resourceIds = data.resources as string[]
      }
    }

    if (isArrayLike(data.invoices) && data.invoices.length > 0) {
      if (isObjectLike(data.invoices[0])) {
        this.invoices = orderBy(
          map(data.invoices, (invoiceData) => new InvoiceModel(invoiceData)),
          'createdAt',
          'asc',
        )
      }
    }

    if (data.portals) {
      this.portals = data.portals.map((portal) => new PortalModel(portal))
    }

    if (data.authRules) {
      this.authRules = data.authRules.map((rule) => new AuthorizationRule(rule))
    }

    this.accountId = data.accountId

    if (data.account) {
      this.account = new PatientAccountModel(data.account)
    }

    this.episodeId = data.episodeId

    if (data.episode) {
      this.episode = new EpisodeModel(data.episode)
    }
  }

  /**
   * Breadcrumb text
   */
  get text(): string {
    const eid = this.eid ? ` ${this.eid}` : ''
    if (this.type === Appointment.TYPE.ORDER || !this.startAt) {
      return `Order${eid}`
    } else {
      return `Appointment on ${this.dateAndTime}`
    }
  }

  get url(): string {
    return `/patient/${this.patientId}/appointment/${this.id}`
  }

  get time(): string {
    return this.getStringTime()
  }

  get dateAndTime(): string {
    return this.getDateAndTime()
  }

  get date(): string {
    if (this.startAt) {
      return this.startAt.format('MM/DD/YYYY')
    } else {
      return ''
    }
  }

  get notesHTML(): string {
    if (this.notes) {
      return this.notes.replace(/\n/g, '<br>')
    }
    return ''
  }

  get cancelled(): boolean {
    return this.status === Appointment.STATUS.CANCELLED
  }

  get hasErrors(): boolean {
    return false
  }

  get eligibilityPrimaryStatus(): Appointment.ELIGIBILITY_STATUS {
    return this.eligibilityDetails?.primary?.status || this.eligibilityStatus || Appointment.ELIGIBILITY_STATUS.NONE
  }

  get eligibilityPrimaryId(): string {
    return this.eligibilityDetails?.primary?.id || this.eligibilityId || ''
  }

  get eligibilitySecondaryStatus(): Appointment.ELIGIBILITY_STATUS {
    return this.eligibilityDetails?.secondary?.status || Appointment.ELIGIBILITY_STATUS.NONE
  }

  get intakeInProgress(): boolean {
    if (!this.intakeStatus) {
      return false
    }

    return [
      Appointment.INTAKE_STATUS.NONE,
      Appointment.INTAKE_STATUS.ACKNOWLEDGED,
      Appointment.INTAKE_STATUS.DELIVERED,
      Appointment.INTAKE_STATUS.PENDING,
    ].includes(this.intakeStatus)
  }

  get intakePatientCompleted(): boolean {
    if (!this.intakeStatus) {
      return false
    }

    return [
      Appointment.INTAKE_STATUS.NOT_REQUIRED,
      Appointment.INTAKE_STATUS.SUBMITTED,
      Appointment.INTAKE_STATUS.CONFIRMED,
      Appointment.INTAKE_STATUS.TECH_CONFIRMED,
      Appointment.INTAKE_STATUS.TECH_REVIEW,
      Appointment.INTAKE_STATUS.SYNCING,
      Appointment.INTAKE_STATUS.SYNCED,
    ].includes(this.intakeStatus)
  }

  get paymentStatusText(): string {
    switch (this.paymentStatus) {
      case Appointment.PAYMENT_STATUS.NONE:
        return 'appointment.payment-status-text.none'
      case Appointment.PAYMENT_STATUS.CONFIRMED:
        return 'appointment.payment-status-text.confirmed'
      case Appointment.PAYMENT_STATUS.ON_FILE:
        return 'appointment.payment-status-text.on-file'
      case Appointment.PAYMENT_STATUS.CHARGEABLE:
        return 'appointment.payment-status-text.chargeable'
      case Appointment.PAYMENT_STATUS.PARTIAL:
        return 'appointment.payment-status-text.partial'
      case Appointment.PAYMENT_STATUS.FULL:
        return 'appointment.payment-status-text.full'
      case Appointment.PAYMENT_STATUS.REFUND_DUE:
        return 'appointment.payment-status-text.refund-due'
      default:
        return 'appointment.payment-status-text.default'
    }
  }

  get validInvoices(): InvoiceModel[] {
    const statusInvoiceActive = [
      Invoice.STATUS.CONFIRMED,
      Invoice.STATUS.PAYING,
      Invoice.STATUS.PARTIAL,
      Invoice.STATUS.PAID,
    ]
    return filter(this.invoices || [], (i) =>
      statusInvoiceActive.includes(i.status),
    )
  }

  get hasInvoice(): InvoiceModel | undefined {
    const statusInvoiceActive = [
      Invoice.STATUS.CONFIRMED,
      Invoice.STATUS.PAID,
      Invoice.STATUS.PARTIAL,
    ]
    const invoice = findLast(this.invoices || [], (i) =>
      includes(statusInvoiceActive, i.status),
    )
    return invoice
  }

  get vip(): boolean {
    return !(this.patient && this.patient.vip)
  }

  public get isCanceled(): boolean {
    return [
      Appointment.STATUS.ABORTED,
      Appointment.STATUS.CANCELLED,
    ].includes(this.status)
  }

  isOrder() {
    return !this.startAt || this.type === Appointment.TYPE.ORDER
  }

  get confirmationStatusSortKey(): number | undefined {
    if (this.patientConfirmed === true) {
      return 1
    } else if (this.patientConfirmed === false) {
      return 0
    } else if (
      this.patientConfirmed === undefined &&
      this.lastConfirmMsgSentAt !== undefined
    ) {
      return 2
    } else if (
      this.patientConfirmed === undefined &&
      this.lastConfirmMsgSentAt === undefined
    ) {
      return 3
    }

    return undefined
  }

  get isSurveySent(): boolean {
    if (!this.surveyStatus) {
      return false
    }

    return [
      Appointment.SURVEY_STATUS.PENDING,
      Appointment.SURVEY_STATUS.SUBMITTED,
    ].includes(this.surveyStatus)
  }

  convertToFacilityTime(
    date: moment.Moment,
    facility?: Facility,
  ): moment.Moment {
    if (facility?.timezone) {
      return date.tz(facility.timezone)
    } else if (this.location?.timezone) {
      return date.tz(this.location.timezone)
    } else {
      return date.local()
    }
  }

  getDate() {
    if (
      this.startAt &&
      (!this.endAt || !this.endAt.isValid() || this.startAt.isSame(this.endAt))
    ) {
      return this.startAt.local().format(`${dayFormat}`)
    }

    if (this.startAt && this.endAt) {
      const sameDay = this.startAt.dayOfYear() === this.endAt.dayOfYear()

      if (sameDay) {
        return this.startAt.local().format(dayFormat)
      } else {
        const start = this.startAt.local().format(`${dayFormat}`)
        const end = this.endAt.local().format(`MM/DD`)
        return `${start} - ${end}`
      }
    }

    return ''
  }

  getStringTime(
    facility?: Facility,
    which: 'startAt' | 'endAt' = 'startAt',
  ): string {
    const date = this.convertToFacilityTime(this[which]!.clone(), facility)
    return date.format(timeFormat)
  }

  getTime(which: 'startAt' | 'endAt' = 'startAt'): moment.Moment {
    const date = this.convertToFacilityTime(this[which]!.clone(), this.location)
    return date
  }

  getTimeTooltip(facility?: Facility): string | undefined {
    if (this.startAt == null) {
      return undefined
    }
    return getFacilityAndLocalTime(this.startAt, {
      facility: facility || this.location,
      tz: this.timezone,
    })
  }

  getDateAndTime(facility?: Facility) {
    if (this.startAt == null) {
      return ''
    }

    const startAt = this.convertToFacilityTime(this.startAt.clone(), facility)
    const endAt =
      this.endAt && this.endAt.isValid()
        ? this.convertToFacilityTime(this.endAt.clone(), facility)
        : undefined

    if (startAt) {
      if (!endAt || startAt.isSame(endAt, 'minute')) {
        return startAt.format(`${dayFormat} ${timeFormat}`)
      } else if (endAt && this.endAt) {
        const sameDay = startAt.dayOfYear() === this.endAt.dayOfYear()
        const start = startAt.format(`${dayFormat} ${timeFormat}`)
        const end = endAt.format(sameDay ? timeFormat : `MM/DD ${timeFormat}`)
        return `${start} - ${end}`
      }
    }

    return ''
  }

  upsertInsurance(insurance: PatientInsurance) {
    const index = this.insurances.findIndex((ins) => ins.id === insurance.id)
    if (index >= 0) {
      this.insurances[index] = insurance
    } else {
      this.insurances.push(insurance)
    }
  }

  removeInsurance(insurance: PatientInsurance) {
    pull(this.insurances, insurance)
  }

  getCptCodes(): string[] {
    if (!this.products || this.products.length === 0) {
      return []
    }

    return this.products
      .flatMap((p) => p.getCPTCodes())
      .filter((code, index, arr) => arr.indexOf(code) === index)
  }

  override toJSON(): any {
    let products: any = this.productIds
    let resources: any = this.resourceIds
    let transactions: any = this.transactionIds

    if (this.products.length > 0) {
      products = this.products.map((product) => product.id)
    }

    if (this.resources.length > 0) {
      resources = this.resources.map((resource) => resource.id)
    }

    if (this.transactions.length > 0) {
      transactions = this.transactions.map((transaction) => transaction.id)
    }

    const json = extend(super.toJSON(), {
      patient: this.patientId,
      accountId: this.accountId,
      newAccount: this.newAccount,
      locationId: this.locationId,
      referrerId: this.referrerId,
      referringPhysicianId: this.referringPhysicianId,
      attendingPhysicianId: this.attendingPhysicianId,
      type: this.type,
      diagnosisCodes: this.diagnosisCodes,
      startAt: this.startAt?.toISOString(),
      endAt: this.endAt?.toISOString(),
      duration: this.duration,
      status: this.status,
      statusDescription: this.statusDescription,
      priority: this.priority,
      orderingPhysician: this.orderingPhysician,
      notes: this.notes,
      waitList: this.waitList,
      workItems: this.workItems,
      intakeId: this.intakeId,
      intakeStatus: this.intakeStatus,
      eligibilityId: this.eligibilityId,
      eligibilities: this.eligibilities,
      eligibilityStatus: this.eligibilityStatus,
      paymentStatus: this.paymentStatus,
      referralStatuses: this.referralStatuses,
      authorizationStatuses: this.authorizationStatuses,
      estimationStatus: this.estimationStatus,
      insurances: this.insurances,
      resources,
      products,
      transactions,
      customFields: this.customFields,
      studies: this.studies,
    })

    // A model built from form values has no studies; sending `[]` would ask the API to replace the
    // stored ones.
    if (!this.studies?.length) {
      delete json.studies
    }

    return json
  }
}

export namespace Appointment {
  export enum TYPE {
    APPOINTMENT = 'A',
    ORDER = 'O',
  }

  export interface EligibilityDetails {
    primary?: InsuranceEligibility
    secondary?: InsuranceEligibility
    tertiary?: InsuranceEligibility
  }

  export interface WaitList {
    status: APPOINTMENT_WAIT_LIST_STATUS
    messageBy?: string
    messageAt?: Date
    createdAt: Date
  }

  export interface InsuranceEligibility {
    id?: string
    status: ELIGIBILITY_STATUS
  }

  export enum STATUS {
    OPEN = 'O',
    HOLD = 'H',
    CANCELLED = 'C',
    RESCHEDULE = 'R',
    ARRIVED = 'A',
    CHECKING_IN = 'CI',
    CHECKED_IN = 'W',
    IN_PROGRESS = 'I',
    ABORTED = 'B',
    COMPLETED = 'D',
    NO_SHOW = 'N',
    RESULTS_READY = 'RR',
  }

  export enum PAYMENT_STATUS {
    NONE = 'N',
    CONFIRMED = 'C',
    ON_FILE = 'I',
    CHARGEABLE = 'A',
    PARTIAL = 'P',
    FULL = 'F',
    REFUND_DUE = 'R',
  }

  export enum INTAKE_STATUS {
    NONE = 'N',
    ACKNOWLEDGED = 'A',
    DELIVERED = 'D',
    PENDING = 'P',
    SUBMITTED = 'S',
    CONFIRMED = 'C',
    TECH_CONFIRMED = 'T',
    TECH_REVIEW = 'W',
    SYNCING = 'U',
    SYNCED = 'Y',
    NOT_REQUIRED = 'R',
  }

  export enum ELIGIBILITY_STATUS {
    NONE = 'N',
    HARD_FAIL = 'F',
    SOFT_FAIL = 'E',
    INVALID = 'IV',
    INACTIVE = 'I',
    ACTIVE = 'V',
    MISMATCH = 'M',
    NOT_REQUIRED = 'A',
    NO_CONNECTION = 'NC',
  }

  export enum INSURANCE_TYPE {
    PRIMARY = 'P',
    SECONDARY = 'S',
    TERTIARY = 'T',
  }

  export enum SURVEY_STATUS {
    NONE = 'N',
    PENDING = 'P',
    SUBMITTED = 'S',
  }

  export enum ESTIMATION_STATUS {
    NONE = 'N',
    DRAFT = 'D',
    CONFIRMED = 'C',
    DELIVERED = 'DL',
    UNDELIVERED = 'UDL',
    NOT_REQUIRED = 'NR',
  }
}
